import { MonitorAlertFullDto } from '@/app/Alert/types';
import { ViewState } from 'react-map-gl';
import timespace from '@mapbox/timespace';

export const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN ?? '';

interface MapboxGeocodingContext {
  id: string;
  text: string;
  [key: string]: unknown;
}

interface MapboxGeocodingFeature {
  center: [number, number];
  place_name: string;
  bbox?: number[];
  context?: MapboxGeocodingContext[];
  properties?: {
    address_line2?: string;
    [key: string]: unknown;
  };
  text?: string;
  address?: string;
  place_type?: string[];
}

interface MapboxGeocodingResponse {
  features: MapboxGeocodingFeature[];
}

export function getTimezoneFromCoordinates(longitude: number, latitude: number): string {
  try {
    const timestamp = Date.now();
    const tzInfo = timespace.getFuzzyLocalTimeFromPoint(timestamp, [longitude, latitude]);
    return tzInfo?._z?.name || '';
  } catch (error) {
    console.error('Failed to get timezone:', error);
    return '';
  }
}

export enum AlertPointType {
  SiteLocation = 'SiteLocation',
  CameraLocation = 'CameraLocation',
}

export interface AlertLocationPoint {
  type: AlertPointType;
  longitude: number | undefined;
  latitude: number | undefined;
  title: string | undefined;
  description: string;
}

export interface AlertLocations {
  siteLocation: AlertLocationPoint;
  startLocation: Partial<ViewState>;
}

export interface GeocodingResult {
  center: [number, number];
  place_name: string;
  bbox?: number[];
  address?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postcode?: string;
  timezone?: string;
}

export function extractLocations(alertData: MonitorAlertFullDto): AlertLocations {
  const siteLocation: AlertLocationPoint = {
    type: AlertPointType.SiteLocation,
    longitude: alertData.project?.address?.lon,
    latitude: alertData.project?.address?.lat,
    title: alertData.projectName,
    description: 'Site Location',
  };

  const startLocation = {
    longitude: siteLocation.longitude,
    latitude: siteLocation.latitude,
    zoom: 17,
    pitch: 0,
    bearing: 0,
  };

  return {
    siteLocation,
    startLocation,
  };
}

export function generateStreetviewUrl(alp: AlertLocationPoint) {
  return encodeURI(
    `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${alp.latitude},${alp.longitude}&fov=100`
  );
}

export async function searchAddress(query: string): Promise<GeocodingResult[]> {
  if (!mapboxToken) return [];
  if (!mapboxToken) {
    throw new Error('Mapbox token is not configured');
  }

  const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
    query
  )}.json?access_token=${mapboxToken}&limit=5`;

  const response = await fetch(endpoint);
  if (!response.ok) {
    throw new Error('Failed to fetch geocoding results');
  }

  const data: MapboxGeocodingResponse = await response.json();

  return data.features.map((feature) => {
    // Initialize result with basic properties
    const result: GeocodingResult = {
      center: feature.center,
      place_name: feature.place_name,
      bbox: feature.bbox,
    };

    // Parse address components from context array
    if (feature.context) {
      feature.context.forEach((ctx) => {
        if (ctx.id.startsWith('place')) {
          result.city = ctx.text;
        } else if (ctx.id.startsWith('region')) {
          result.state = ctx.text;
        } else if (ctx.id.startsWith('country')) {
          result.country = ctx.text;
        } else if (ctx.id.startsWith('postcode')) {
          result.postcode = ctx.text;
        }
      });
    }

    // Extract street address (address line 1)
    if (feature.properties) {
      // Get address line 2 if available
      if (feature.properties.address_line2) {
        result.address_line2 = feature.properties.address_line2;
      }
    }

    // Handle street address with number
    let streetAddress = '';

    // Get the house number if available
    if (feature.address) {
      streetAddress = feature.address + ' ';
    }

    // Add the street name
    if (feature.text) {
      streetAddress += feature.text;
    }

    // If we have a street address, use it
    if (streetAddress.trim()) {
      result.address = streetAddress.trim();
    }
    // Fallback to splitting place_name if we still don't have an address
    else if (feature.place_type && feature.place_type.includes('address')) {
      const addressParts = feature.place_name.split(',');
      if (addressParts.length > 0) {
        result.address = addressParts[0].trim();
      }
    }

    return result;
  });
}
